/* ===========================================================
   editor/EditorView.jsx — the page you write on

   A TipTap (ProseMirror) editor bound to the doc's Yjs document, so
   every keystroke is a Yjs update (saved here, then synced live), and
   other people's cursors show with their names.
   =========================================================== */

import { Autocorrect } from './autocorrect.js';
import { pref } from '../state.js';
import { setActiveEditor } from '../ui/context.jsx';
import { useEffect, useRef } from 'preact/hooks';
import { Editor } from '@tiptap/core';
import { Collaboration } from '@tiptap/extension-collaboration';
import { CollaborationCaret } from '@tiptap/extension-collaboration-caret';
import { docExtensions } from './extensions.js';
import { uploadImage } from './images.js';
import { toast } from '../ui/ui.jsx';

function caret(user) {
  const el = document.createElement('span');
  el.className = 'collab-caret';
  el.style.borderColor = user.color;
  const label = document.createElement('span');
  label.className = 'collab-label';
  label.style.background = user.color;
  label.textContent = user.name;
  el.append(label);
  return el;
}

async function insertImages(editor, docId, files, pos) {
  for (const file of files) {
    const id = toast('Adding picture…', { ms: 20000 });
    try {
      const src = await uploadImage(docId, file);
      const at = pos ?? editor.state.selection.to;
      editor.chain().focus().insertContentAt(at, { type: 'image', attrs: { src, alt: file.name?.replace(/\.\w+$/, '') || '' } }).run();
    } catch (err) {
      toast(err, { kind: 'error' });
    } finally {
      import('../ui/ui.jsx').then(m => m.dismiss(id));
    }
  }
}

export function EditorView({ sync, editable, user, docId, mode, onEditor, spellcheck = true, font = 'sans', field = 'default' }) {
  const host = useRef();
  useEffect(() => {
    const ed = new Editor({
      element: host.current,
      editable,
      extensions: [
        ...docExtensions({ placeholder: editable ? 'Start writing…' : '' }),
        Autocorrect.configure({ enabled: () => editable && pref('autocorrect', true) }),
        Collaboration.configure({ document: sync.doc, field }),   // the open tab (editor/tabs.js)
        CollaborationCaret.configure({ provider: sync, user, render: caret }),
      ],
      editorProps: {
        attributes: {
          class: `prose font-${font}`,
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': 'Document text',
          spellcheck: spellcheck ? 'true' : 'false',
        },
        handlePaste(view, event) {
          const files = [...(event.clipboardData?.files || [])].filter(f => f.type.startsWith('image/'));
          if (!files.length || !view.editable) return false;
          insertImages(ed, docId, files);
          return true;
        },
        handleDrop(view, event, _slice, moved) {
          if (moved || !view.editable) return false;
          const files = [...(event.dataTransfer?.files || [])].filter(f => f.type.startsWith('image/'));
          if (!files.length) return false;
          const at = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
          insertImages(ed, docId, files, at);
          return true;
        },
      },
    });
    onEditor?.(ed);
    setActiveEditor(ed);
    return () => { onEditor?.(null); setActiveEditor(null); ed.destroy(); };
  }, [sync, mode, font, spellcheck, field]);

  return <div ref={host} class="editor-host" />;
}

export { insertImages };
